import { useState } from 'react';
import { Link } from 'react-router-dom';
import { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useComments } from '@/hooks/useComments';
import { CommentForm } from './CommentForm';
import { NoteContent } from '@/components/NoteContent';
import { ZapButton } from '@/components/ZapButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MessageSquare, ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { genUserName } from '@/lib/genUserName';

interface CommentProps {
  root: NostrEvent | URL;
  comment: NostrEvent;
  depth?: number;
  maxDepth?: number;
  limit?: number;
}

export function Comment({ root, comment, depth = 0, maxDepth = 3, limit }: CommentProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(depth < 2); // Auto-expand first 2 levels

  const author = useAuthor(comment.pubkey);
  const { data: commentsData } = useComments(root, limit);

  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(comment.pubkey)
  const timeAgo = formatDistanceToNow(new Date(comment.created_at * 1000), { addSuffix: true });

  // Get direct replies to this comment
  const replies = commentsData?.getDirectReplies(comment.id) || [];
  const hasReplies = replies.length > 0;

  // Check if this is a reply to another comment
  // Find the lowercase 'e' tag (reply to specific comment)
  const replyTag = comment.tags.find(([name]) => name === 'e');
  const isReply = !!replyTag;

  // Get the parent comment if this is a reply
  const parentComment = isReply && commentsData?.allComments && replyTag
    ? commentsData.allComments.find(c => c.id === replyTag[1])
    : null;
  const parentAuthor = useAuthor(parentComment?.pubkey || '');
  const parentMetadata = parentAuthor.data?.metadata;
  const parentDisplayName = parentMetadata?.name ?? (parentComment ? genUserName(parentComment.pubkey) : '');

  return (
    <div className="space-y-2">
      <Card className="glass border-0">
        <CardContent className="p-3">
          <div className="space-y-1.5">
            {/* Comment Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Link to={`/${nip19.npubEncode(comment.pubkey)}`}>
                  <Avatar className="h-7 w-7 hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer">
                    <AvatarImage src={metadata?.picture} />
                    <AvatarFallback className="text-xs">
                      {displayName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <Link
                  to={`/${nip19.npubEncode(comment.pubkey)}`}
                  className="font-medium text-sm hover:text-primary transition-colors"
                >
                  {displayName}
                </Link>
              </div>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>

            {/* Quoted Reply Context */}
            {isReply && parentComment && (
              <div className="pl-9 mb-2">
                <div className="flex items-center gap-2 p-1 rounded-full border border-white/20 bg-white/5 w-full">
                  <Avatar className="h-5 w-5 flex-shrink-0">
                    <AvatarImage src={parentMetadata?.picture} />
                    <AvatarFallback className="text-[10px]">
                      {parentDisplayName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-white/60 truncate flex-1">
                    {parentComment.content}
                  </span>
                </div>
              </div>
            )}

            {/* Comment Content */}
            <div className="text-sm pl-9">
              <NoteContent event={comment} className="text-sm" />
            </div>

            {/* Comment Actions */}
            <div className="flex items-center justify-between pl-9">
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReplyForm(!showReplyForm)}
                  className="h-7 w-7 p-0 bg-white/5 hover:bg-white/10"
                  aria-label="Reply"
                >
                  <MessageSquare className="h-3 w-3" />
                </Button>

                <ZapButton
                  event={comment}
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 bg-white/5 hover:bg-white/10"
                />

                {hasReplies && (
                  <Collapsible open={showReplies} onOpenChange={setShowReplies}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                        {showReplies ? (
                          <ChevronDown className="h-3 w-3 mr-1" />
                        ) : (
                          <ChevronRight className="h-3 w-3 mr-1" />
                        )}
                        {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                      </Button>
                    </CollapsibleTrigger>
                  </Collapsible>
                )}
              </div>

              {/* Comment menu */}
              <div className="flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      aria-label="Comment options"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reply Form */}
      {showReplyForm && (
        <div className="ml-6">
          <CommentForm
            root={root}
            reply={comment}
            onSuccess={() => setShowReplyForm(false)}
            placeholder="Write a reply..."
            compact
          />
        </div>
      )}

      {/* Replies */}
      {hasReplies && (
        <Collapsible open={showReplies} onOpenChange={setShowReplies}>
          <CollapsibleContent className="space-y-2">
            {replies.map((reply) => (
              <Comment
                key={reply.id}
                root={root}
                comment={reply}
                depth={depth + 1}
                maxDepth={maxDepth}
                limit={limit}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}